import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { APIProvider } from "@vis.gl/react-google-maps";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

/**
 * Context exposing Google Maps runtime state across the app so any
 * <Map /> consumer can render a clean fallback instead of the broken
 * "This page can't load Google Maps correctly" overlay.
 *
 *  - apiKeyMissing : true if REACT_APP_GOOGLE_MAPS_API_KEY is empty
 *  - authFailed    : true if Google's gm_authFailure global fired
 *                    (invalid key, referrer block, billing issue)
 *  - ready         : true once the API key is configured and no auth
 *                    failure has been reported
 */
const GoogleMapsCtx = createContext({
  apiKeyMissing: !API_KEY,
  authFailed: false,
  ready: !!API_KEY,
});

export function useGoogleMapsStatus() {
  return useContext(GoogleMapsCtx);
}

/**
 * Lazy provider — wraps Google Maps API loader exactly once at app root,
 * so any <Map /> instance inside the tree shares the same loaded script.
 *
 * Also installs a global `gm_authFailure` listener so failures surface
 * cleanly inside any consuming component (no ugly Google overlay).
 */
export default function GoogleMapsProvider({ children }) {
  const [authFailed, setAuthFailed] = useState(false);

  useEffect(() => {
    // Google calls window.gm_authFailure() when key is invalid / domain
    // not authorized / billing not enabled. We intercept and broadcast it.
    if (typeof window === "undefined") return;
    const prev = window.gm_authFailure;
    window.gm_authFailure = () => {
      setAuthFailed(true);
      try { prev && prev(); } catch { /* swallow */ }
      // Also hide Google's injected error overlay so our clean UI shows through.
      try {
        document.querySelectorAll(
          ".gm-err-container, .gm-err-content, .gm-err-message, .gm-err-title"
        ).forEach((el) => { el.style.display = "none"; });
      } catch { /* noop */ }
    };
    return () => { window.gm_authFailure = prev; };
  }, []);

  // Style block to keep Google's auth-error overlay from breaking layout.
  // Once authFailed is true our components render their own fallback UI,
  // so this just hides Google's red banner.
  useEffect(() => {
    if (!authFailed) return;
    const style = document.createElement("style");
    style.setAttribute("data-zmex-maps-error", "1");
    style.textContent = `
      .gm-err-container, .gm-err-content, .gm-err-message,
      .gm-err-title, .gm-err-autocomplete { display: none !important; }
    `;
    document.head.appendChild(style);
    return () => { try { document.head.removeChild(style); } catch { /* noop */ } };
  }, [authFailed]);

  const value = useMemo(
    () => ({ apiKeyMissing: !API_KEY, authFailed, ready: !!API_KEY && !authFailed }),
    [authFailed]
  );

  if (!API_KEY) {
    return (
      <GoogleMapsCtx.Provider value={value}>
        {children}
      </GoogleMapsCtx.Provider>
    );
  }
  return (
    <GoogleMapsCtx.Provider value={value}>
      <APIProvider apiKey={API_KEY} libraries={["places"]}>
        {children}
      </APIProvider>
    </GoogleMapsCtx.Provider>
  );
}

export const GOOGLE_MAPS_MAP_ID =
  process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || "ZMEX_HUB_MAIN";
// Production fallback center — Taft, Florida 32824
export const TAFT_CENTER = { lat: 28.4119, lng: -81.5629 };
// Kept for backward compatibility
export const KISSIMMEE_CENTER = TAFT_CENTER;

export function googleMapsDirectionsUrl({ lat, lng, address }) {
  const dest = lat != null && lng != null ? `${lat},${lng}` : encodeURIComponent(address || "");
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
}
